import { Link } from '@ksyos/design-system';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import type {
    DecisionAidProps as DecisionAidComponentProps,
    DecisionAidPlacement,
} from '@frontend/components/elements/DecisionAid';
import type { IService } from '@frontend/components/pages/home/newProcess/newProcessInterfaces';
import type { IReduxState } from '@frontend/configureStore';
import { EpisodeCategory, ServiceType } from '@common/enums';
import { DecisionAid } from '@frontend/components/elements/DecisionAid';
import {
    getDecisionAidSteps,
    getUserPreferenceSettingName,
} from '@frontend/components/pages/decisionAidSteps/getDecisionAidSteps';
import { fetchJson } from '@frontend/store/fetch';

interface IUseDecisionAidComponentProps {
    placement?: DecisionAidPlacement;
    selectedEpisodeCategory: EpisodeCategory | undefined;
    selectedServiceType: ServiceType;
    selectedStep: string | undefined;
    services: IService[];
}

export function useDecisionAidComponent(options: IUseDecisionAidComponentProps) {
    const { selectedEpisodeCategory, selectedServiceType, services, selectedStep } = options;
    const contextPractitionerId = useSelector((state: IReduxState) => state.authentication.id);
    const settingName = getUserPreferenceSettingName(selectedEpisodeCategory, selectedServiceType);

    const [hasCheckbox, setHasCheckbox] = useState<boolean | undefined>(undefined);
    const [isChecked, setIsChecked] = useState(false);

    const [hasDecisionAid, setHasDecisionAid] = useState(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const steps = useMemo(() => {
        return getDecisionAidSteps(selectedEpisodeCategory, selectedServiceType, services, options.placement);
    }, [selectedEpisodeCategory, selectedServiceType, services]);

    const DecisionAidProps = useMemo(
        (): DecisionAidComponentProps => ({
            isOpen,
            onClose: () => setIsOpen(false),
            steps,
            selectedStep,
            toolTipCheckboxProps: {
                hasCheckbox: !!hasCheckbox,
                isChecked: !!isChecked,
                onChange: setIsChecked,
            },
        }),
        [hasCheckbox, isChecked, isOpen, steps, selectedStep, updateUserPreference]
    );

    const decisionAidLink = useMemo(() => {
        if (hasDecisionAid) {
            return (
                <Link
                    mt={2}
                    onClick={() => setIsOpen(true)}
                    variant="button"
                >
                    Keuzehulp
                </Link>
            );
        }

        return undefined;
    }, [hasDecisionAid]);

    useEffect(() => {
        setIsOpen(hasDecisionAid && (isOpen || !!hasCheckbox));
    }, [hasDecisionAid, hasCheckbox]);

    useEffect(() => {
        setHasDecisionAid(
            selectedEpisodeCategory === EpisodeCategory.mentalHealth && selectedServiceType === ServiceType.consultation
        );
    }, [selectedEpisodeCategory, selectedServiceType]);

    useEffect(() => {
        getUserPreference();
    }, [contextPractitionerId, hasDecisionAid]);

    useEffect(() => {
        if (!isOpen && isChecked) {
            updateUserPreference();
        }
    }, [isOpen, isChecked]);

    async function getUserPreference() {
        if (settingName) {
            const result = await fetchJson(`/api/userPreference/${contextPractitionerId}/${settingName}`, 'GET');

            setHasCheckbox(!result?.json?.userPreference?.[settingName]);
        }
    }

    function updateUserPreference() {
        if (contextPractitionerId) {
            const body = {
                userPreferences: {
                    [String(settingName)]: true,
                },
            };

            fetchJson(`/api/userPreference/${contextPractitionerId}`, 'POST', body);
            setHasCheckbox(false);
        }
    }

    return {
        DecisionAidProps,
        DecisionAid,
        decisionAidLink,
        hasDecisionAid,
        setIsDecisionAidOpen: setIsOpen,
    };
}
